import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import * as THREE from 'three';
import {
  advanceYardPhysics,
  ballPosition,
  ballTransformOffset,
  beginDrag,
  boomAt,
  brickBodyIdAt,
  cannonballTransformOffset,
  cascadeLaunch,
  calmWreckingBall,
  cascadeRubbleReadyToSleep,
  countAirborneMovingCascadeBricks,
  forceSleepAllCascadeBricks,
  sleepSettledCascadeBricks,
  chainTransformOffset,
  getChainStability,
  resetChainStability,
  BALL_RADIUS,
  BRICK_FULL,
  CANNONBALL_RADIUS,
  CHAIN_ANCHOR,
  CHAIN_LINKS,
  LINK_HALF_HEIGHT,
  LINK_RADIUS,
  MAX_BRICKS,
  MAX_CANNONBALLS,
  endDrag,
  getYardRuntime,
  getSleepHeatmapColor,
  resetYard,
  setYardStructure,
  shouldUseYardPhysicsWorker,
  spawnCannonball,
  updateDragTarget,
  type YardRuntime,
  type YardStructure,
  type YardTool,
} from '../physics/yardPhysics';
import { YardWorkerClient } from '../physics/yardWorkerClient';
import './WreckingYard.css';
import '../components/MobileLabChrome.css';

interface YardState {
  canvasReady: boolean;
  physicsReady: boolean;
  bodyCount: number;
  brickCount: number;
  awakeCount: number;
  tool: YardTool;
  structure: YardStructure;
  /** Optional: bricks that have been awake at least once (cascade coverage). */
  everAwakeCount?: number;
}

declare global {
  interface Window {
    __yardState?: YardState;
  }
}

function writeYardState(patch: Partial<YardState>) {
  const previous = window.__yardState;
  window.__yardState = {
    canvasReady: patch.canvasReady ?? previous?.canvasReady ?? false,
    physicsReady: patch.physicsReady ?? previous?.physicsReady ?? false,
    bodyCount: patch.bodyCount ?? previous?.bodyCount ?? 0,
    brickCount: patch.brickCount ?? previous?.brickCount ?? 0,
    awakeCount: patch.awakeCount ?? previous?.awakeCount ?? 0,
    tool: patch.tool ?? previous?.tool ?? 'ball',
    structure: patch.structure ?? previous?.structure ?? 'tower',
    everAwakeCount: patch.everAwakeCount ?? previous?.everAwakeCount,
  };
}

interface YardStats {
  bodies: number;
  bricks: number;
  awake: number;
  awakeBricks: number;
  stepMs: number;
  idleMs: number;
  activeMs: number;
  fps: number;
}

interface YardFx {
  impact: (x: number, y: number, z: number, strength: number) => void;
  explosion: (x: number, y: number, z: number) => void;
}

// ---------- shared textures ----------

function makeGlowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grad = ctx.createRadialGradient(64, 64, 2, 64, 64, 64);
    grad.addColorStop(0, 'rgba(255, 236, 190, 1)');
    grad.addColorStop(0.28, 'rgba(255, 200, 110, 0.55)');
    grad.addColorStop(0.65, 'rgba(255, 160, 70, 0.12)');
    grad.addColorStop(1, 'rgba(255, 150, 60, 0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 128, 128);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function makeGroundTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Cool near-black asphalt — the warm pools must come from the lights,
    // not from a brown base color.
    ctx.fillStyle = '#101318';
    ctx.fillRect(0, 0, 256, 256);
    for (let i = 0; i < 220; i += 1) {
      const x = Math.random() * 256;
      const y = Math.random() * 256;
      const w = 2 + Math.random() * 22;
      const h = 1 + Math.random() * 3;
      const v = Math.random();
      ctx.fillStyle = v < 0.45 ? 'rgba(190, 200, 220, 0.05)' : 'rgba(0, 0, 0, 0.22)';
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.random() * Math.PI);
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.restore();
    }
    // paving seams
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
    ctx.lineWidth = 3;
    for (let p = 0; p <= 256; p += 64) {
      ctx.beginPath();
      ctx.moveTo(p, 0);
      ctx.lineTo(p, 256);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, p);
      ctx.lineTo(256, p);
      ctx.stroke();
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(9, 9);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// ---------- scene dressing ----------

function Ground() {
  const texture = useMemo(() => makeGroundTexture(), []);
  useEffect(() => () => texture.dispose(), [texture]);

  // Sparse raised paving patches suggesting slab seams.
  const patches = useMemo(() => {
    const list: Array<{ x: number; z: number; w: number; d: number; dark: boolean; rot: number }> = [];
    for (let i = 0; i < 70; i += 1) {
      const a = seedRand(i * 5 + 3) * Math.PI * 2;
      const r = 6 + seedRand(i * 7 + 1) * 26;
      list.push({
        x: Math.cos(a) * r,
        z: Math.sin(a) * r,
        w: 1.2 + seedRand(i * 11 + 2) * 2.2,
        d: 1.2 + seedRand(i * 13 + 4) * 2.2,
        dark: seedRand(i * 17 + 5) < 0.5,
        rot: seedRand(i * 19 + 6) * 0.4 - 0.2,
      });
    }
    return list;
  }, []);

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[130, 130]} />
        <meshStandardMaterial map={texture} color="#c6ccd8" roughness={0.95} metalness={0.02} />
      </mesh>
      {patches.map((patch, i) => (
        <mesh key={i} position={[patch.x, 0.025, patch.z]} rotation={[0, patch.rot, 0]} receiveShadow>
          <boxGeometry args={[patch.w, 0.05, patch.d]} />
          <meshStandardMaterial color={patch.dark ? '#14161c' : '#191c23'} roughness={0.96} />
        </mesh>
      ))}
    </group>
  );
}

/** Static stadium/city-block dressing around the 4x4 dynamic building grid. */
function CityBlockDressing() {
  const sideStands = useMemo(() => [-1, 1].flatMap((side) => [
    { x: side * 16.2, z: -5.4, rot: side < 0 ? 0 : Math.PI },
    { x: side * 16.2, z: 0, rot: side < 0 ? 0 : Math.PI },
    { x: side * 16.2, z: 5.4, rot: side < 0 ? 0 : Math.PI },
  ]), []);
  const endStands = useMemo(() => [-1, 1].flatMap((side) => [
    { x: -7.2, z: side * 16.2, rot: side > 0 ? -Math.PI / 2 : Math.PI / 2 },
    { x: 0, z: side * 16.2, rot: side > 0 ? -Math.PI / 2 : Math.PI / 2 },
    { x: 7.2, z: side * 16.2, rot: side > 0 ? -Math.PI / 2 : Math.PI / 2 },
  ]), []);
  const towers = useMemo(() => [
    [-18.4, -18.4],
    [18.4, -18.4],
    [-18.4, 18.4],
    [18.4, 18.4],
  ] as const, []);

  const renderStand = (stand: { x: number; z: number; rot: number }, i: number, width: number) => (
    <group key={i} position={[stand.x, 0, stand.z]} rotation={[0, stand.rot, 0]}>
      {Array.from({ length: 7 }, (_, row) => (
        <mesh key={row} position={[row * 0.18, 0.22 + row * 0.26, 0]} castShadow receiveShadow>
          <boxGeometry args={[2.9, 0.24, width]} />
          <meshStandardMaterial color={row % 2 ? '#3a4356' : '#252d3d'} roughness={0.88} />
        </mesh>
      ))}
    </group>
  );

  return (
    <group>
      <mesh position={[0, 0.035, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <circleGeometry args={[8.8, 96]} />
        <meshStandardMaterial color="#224832" roughness={0.92} metalness={0} />
      </mesh>
      <mesh position={[0, 0.05, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.38, 0.74, 1]} receiveShadow>
        <torusGeometry args={[9.9, 0.34, 12, 144]} />
        <meshStandardMaterial color="#9b4f37" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]} scale={[1.44, 0.78, 1]} receiveShadow>
        <torusGeometry args={[11.4, 0.2, 10, 144]} />
        <meshStandardMaterial color="#d8c48b" roughness={0.85} />
      </mesh>
      <mesh position={[0, 0.045, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <ringGeometry args={[15.8, 16.5, 144]} />
        <meshStandardMaterial color="#566071" roughness={0.88} />
      </mesh>
      {sideStands.map((stand, i) => renderStand(stand, i, 5.1))}
      {endStands.map((stand, i) => renderStand(stand, i + sideStands.length, 6.6))}
      {towers.map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh position={[0, 3.3, 0]} castShadow>
            <boxGeometry args={[0.35, 6.6, 0.35]} />
            <meshStandardMaterial color="#596170" roughness={0.68} metalness={0.25} />
          </mesh>
          <mesh position={[0, 6.85, 0]} castShadow>
            <boxGeometry args={[1.5, 0.45, 1.1]} />
            <meshStandardMaterial color="#dae7ff" emissive="#c9dbff" emissiveIntensity={0.45} roughness={0.4} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function YardDressing() {
  // Static decorative bricks: loose debris piles + two neat pallets.
  const meshRef = useRef<THREE.InstancedMesh | null>(null);
  const items = useMemo(() => {
    const list: Array<{ x: number; y: number; z: number; yaw: number; tint: string }> = [];
    const palette = ['#b0472f', '#c25a38', '#a03e2a', '#d9a53f', '#e2cfa8'];
    const pick = (seed: number) => palette[Math.floor(seedRand(seed) * palette.length) % palette.length];
    // debris piles
    const piles: Array<[number, number, number]> = [
      [-7.5, -6.5, 16],
      [7.5, -6, 13],
      [-9, 5.5, 18],
      [4, 9, 12],
    ];
    piles.forEach(([px, pz, count], pi) => {
      for (let i = 0; i < count; i += 1) {
        const a = seedRand(pi * 97 + i * 13 + 1) * Math.PI * 2;
        const r = seedRand(pi * 89 + i * 17 + 2) * 1.4;
        const stacked = seedRand(pi * 83 + i * 19 + 3) < 0.3;
        list.push({
          x: px + Math.cos(a) * r,
          y: 0.16 + (stacked ? 0.33 : 0),
          z: pz + Math.sin(a) * r,
          yaw: seedRand(pi * 79 + i * 23 + 4) * Math.PI,
          tint: pick(pi * 73 + i * 29 + 5),
        });
      }
    });
    // two neat pallets (4 x 3 x 2 stacks)
    const pallets: Array<[number, number]> = [
      [-11.5, 1.5],
      [10.5, -8.5],
    ];
    pallets.forEach(([px, pz], pi) => {
      for (let ix = 0; ix < 4; ix += 1) {
        for (let iz = 0; iz < 3; iz += 1) {
          for (let iy = 0; iy < 2; iy += 1) {
            list.push({
              x: px + ix * 0.47,
              y: 0.22 + iy * 0.33,
              z: pz + iz * 0.47,
              yaw: 0,
              tint: pick(pi * 61 + ix * 7 + iz * 11 + iy * 13),
            });
          }
        }
      }
    });
    return list;
  }, []);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const dummy = new THREE.Object3D();
    const color = new THREE.Color();
    items.forEach((item, i) => {
      dummy.position.set(item.x, item.y, item.z);
      dummy.rotation.set(0, item.yaw, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, color.set(item.tint).offsetHSL(0, 0, (seedRand(i * 31 + 9) - 0.5) * 0.1));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }, [items]);

  return (
    <group>
      <instancedMesh ref={meshRef} args={[undefined, undefined, items.length]} castShadow receiveShadow>
        <boxGeometry args={[0.45, 0.32, 0.45]} />
        <meshStandardMaterial roughness={0.85} metalness={0.03} />
      </instancedMesh>
      {/* pallet bases */}
      {[[-10.8, 1.97], [11.2, -8.03]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.04, z]} receiveShadow castShadow>
          <boxGeometry args={[2.3, 0.08, 1.9]} />
          <meshStandardMaterial color="#3a3226" roughness={0.9} />
        </mesh>
      ))}
    </group>
  );
}

function Stars() {
  const positions = useMemo(() => {
    const count = 300;
    const array = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      const a = seedRand(i * 2 + 1) * Math.PI * 2;
      const elevation = 0.1 + seedRand(i * 3 + 2) * 0.85;
      const r = 90;
      array[i * 3] = Math.cos(a) * r * Math.cos(elevation);
      array[i * 3 + 1] = Math.sin(elevation) * r;
      array[i * 3 + 2] = Math.sin(a) * r * Math.cos(elevation);
    }
    return array;
  }, []);
  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#cfd8ef" size={1.6} transparent opacity={0.55} depthWrite={false} sizeAttenuation={false} fog={false} />
    </points>
  );
}

function VoxelClouds() {
  // Big, very dark voxel slabs high up, catching a hint of warm light —
  // like the blocky clouds in yard-hero.png.
  const clusters: Array<{ pos: [number, number, number]; slabs: Array<[number, number, number, number, number, number]> }> = [
    {
      pos: [-22, 21, -26],
      slabs: [
        [0, 0, 0, 16, 1.6, 7],
        [5, 1.4, 2, 10, 1.4, 5],
        [-6, -1.2, 3, 9, 1.2, 4],
      ],
    },
    {
      pos: [18, 24, -20],
      slabs: [
        [0, 0, 0, 14, 1.6, 6],
        [-4, 1.3, 2.5, 8, 1.3, 4],
      ],
    },
    {
      pos: [2, 26, 18],
      slabs: [
        [0, 0, 0, 18, 1.8, 7],
        [7, -1.4, -2, 9, 1.4, 4.5],
      ],
    },
  ];
  return (
    <group>
      {clusters.map((cluster, ci) => (
        <group key={ci} position={cluster.pos}>
          {cluster.slabs.map(([x, y, z, w, h, d], si) => (
            <mesh key={si} position={[x, y, z]}>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color="#10141f" emissive="#1a1208" emissiveIntensity={0.22} roughness={1} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
}

function seedRand(seed: number) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function CitySky() {
  return (
    <group>
      <mesh scale={[1, 0.72, 1]}>
        <sphereGeometry args={[118, 48, 24]} />
        <shaderMaterial
          side={THREE.BackSide}
          depthWrite={false}
          fog={false}
          uniforms={{
            topColor: { value: new THREE.Color('#7fa7d6') },
            midColor: { value: new THREE.Color('#f3b56f') },
            bottomColor: { value: new THREE.Color('#253348') },
          }}
          vertexShader={`
            varying vec3 vWorldPosition;
            void main() {
              vec4 worldPosition = modelMatrix * vec4(position, 1.0);
              vWorldPosition = worldPosition.xyz;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
          `}
          fragmentShader={`
            uniform vec3 topColor;
            uniform vec3 midColor;
            uniform vec3 bottomColor;
            varying vec3 vWorldPosition;
            void main() {
              float h = normalize(vWorldPosition).y * 0.5 + 0.5;
              vec3 horizon = mix(bottomColor, midColor, smoothstep(0.18, 0.55, h));
              vec3 sky = mix(horizon, topColor, smoothstep(0.48, 1.0, h));
              gl_FragColor = vec4(sky, 1.0);
            }
          `}
        />
      </mesh>
      <mesh position={[-36, 22, -48]}>
        <sphereGeometry args={[3.6, 32, 16]} />
        <meshBasicMaterial color="#ffe0a3" fog={false} />
      </mesh>
    </group>
  );
}

function Lighting({ city = false }: { city?: boolean }) {
  const spotTargetA = useMemo(() => {
    const target = new THREE.Object3D();
    target.position.set(0, 4.2, 0);
    return target;
  }, []);
  const spotTargetB = useMemo(() => {
    const target = new THREE.Object3D();
    target.position.set(0.6, 3.2, 0.4);
    return target;
  }, []);

  return (
    <>
      <hemisphereLight args={city ? ['#b7d8ff', '#554333', 1.35] : ['#24386a', '#05060a', 0.6]} />
      <ambientLight intensity={city ? 0.16 : 0.05} color={city ? '#c4d9ff' : '#24386a'} />
      {city && (
        <directionalLight
          position={[-18, 26, -22]}
          intensity={1.9}
          color="#ffe1a3"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-camera-left={-36}
          shadow-camera-right={36}
          shadow-camera-top={36}
          shadow-camera-bottom={-36}
          shadow-camera-near={4}
          shadow-camera-far={80}
          shadow-bias={-0.00025}
        />
      )}
      {/* warm key floodlights, high-left, raking the structure */}
      <primitive object={spotTargetA} />
      <primitive object={spotTargetB} />
      <spotLight
        position={[-10, 9.6, -3.6]}
        target={spotTargetA}
        color={city ? '#ffd18c' : '#ffb54d'}
        intensity={city ? 700 : 1700}
        angle={0.5}
        penumbra={0.6}
        distance={46}
        decay={1.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-bias={-0.0002}
        shadow-camera-near={2}
        shadow-camera-far={45}
      />
      <spotLight
        position={[-7.6, 8.2, 5.4]}
        target={spotTargetB}
        color={city ? '#fff0c0' : '#ffab42'}
        intensity={city ? 460 : 1150}
        angle={0.55}
        penumbra={0.6}
        distance={42}
        decay={1.8}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-bias={-0.0002}
        shadow-camera-near={2}
        shadow-camera-far={40}
      />
      {/* cool rim from behind-right for edge separation */}
      <directionalLight position={[15, 9, -12]} intensity={city ? 0.35 : 0.45} color={city ? '#9ab6ee' : '#5a78c8'} />
    </>
  );
}

function ConeDust({ length, radius }: { length: number; radius: number }) {
  const pointsRef = useRef<THREE.Points | null>(null);
  const { positions, seeds } = useMemo(() => {
    const count = 150;
    const pos = new Float32Array(count * 3);
    const seed = new Float32Array(count);
    for (let i = 0; i < count; i += 1) {
      const t = 0.15 + seedRand(i * 17 + 1) * 0.85;
      const r = seedRand(i * 19 + 2) * radius * t * 0.85;
      const a = seedRand(i * 23 + 3) * Math.PI * 2;
      pos[i * 3] = Math.cos(a) * r;
      pos[i * 3 + 1] = -t * length;
      pos[i * 3 + 2] = Math.sin(a) * r;
      seed[i] = seedRand(i * 29 + 4) * Math.PI * 2;
    }
    return { positions: pos, seeds: seed };
  }, [length, radius]);

  useFrame(({ clock }) => {
    const points = pointsRef.current;
    if (!points) return;
    const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
    const time = clock.elapsedTime;
    for (let i = 0; i < seeds.length; i += 1) {
      const base = positions[i * 3 + 1];
      attr.array[i * 3 + 1] = base + Math.sin(time * 0.35 + seeds[i]) * 0.22;
      attr.array[i * 3] = positions[i * 3] + Math.sin(time * 0.22 + seeds[i] * 1.7) * 0.12;
    }
    attr.needsUpdate = true;
    points.rotation.y = time * 0.03;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions.slice(), 3]} />
      </bufferGeometry>
      <pointsMaterial
        color="#ffd9a0"
        size={0.055}
        transparent
        opacity={0.5}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
        sizeAttenuation
      />
    </points>
  );
}

function Floodlight({
  position,
  headY,
  target,
  glowTexture,
}: {
  position: [number, number, number];
  headY: number;
  target: [number, number, number];
  glowTexture: THREE.Texture;
}) {
  const head = useMemo(() => new THREE.Vector3(position[0], headY, position[2]), [position, headY]);
  const aim = useMemo(() => new THREE.Vector3(...target), [target]);
  const coneLength = useMemo(() => head.distanceTo(aim) + 4, [head, aim]);
  const coneRadius = coneLength * 0.24;
  const coneQuat = useMemo(() => {
    const dir = aim.clone().sub(head).normalize();
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, -1, 0), dir);
  }, [head, aim]);
  const coneGeometry = useMemo(() => {
    const geometry = new THREE.ConeGeometry(coneRadius, coneLength, 26, 1, true);
    geometry.translate(0, -coneLength / 2, 0);
    return geometry;
  }, [coneRadius, coneLength]);
  const innerConeGeometry = useMemo(() => {
    const geometry = new THREE.ConeGeometry(coneRadius * 0.55, coneLength * 0.92, 20, 1, true);
    geometry.translate(0, -coneLength * 0.46, 0);
    return geometry;
  }, [coneRadius, coneLength]);
  useEffect(
    () => () => {
      coneGeometry.dispose();
      innerConeGeometry.dispose();
    },
    [coneGeometry, innerConeGeometry],
  );

  const bulbOffsets = [-0.42, 0, 0.42];
  const bulbRows = [-0.14, 0.14];

  return (
    <group>
      {/* lattice mast */}
      <group position={[position[0], 0, position[2]]}>
        {[-0.16, 0.16].map((off) => (
          <mesh key={`post-${off}`} position={[off, headY / 2, 0]} castShadow>
            <boxGeometry args={[0.09, headY, 0.09]} />
            <meshStandardMaterial color="#252a33" metalness={0.5} roughness={0.42} />
          </mesh>
        ))}
        {Array.from({ length: Math.floor(headY / 1.1) }, (_, i) => (
          <mesh key={`rung-${i}`} position={[0, 0.7 + i * 1.1, 0]} rotation={[0, 0, i % 2 === 0 ? 0.5 : -0.5]}>
            <boxGeometry args={[0.42, 0.055, 0.055]} />
            <meshStandardMaterial color="#2b313c" metalness={0.5} roughness={0.45} />
          </mesh>
        ))}
      </group>
      {/* lamp head assembly */}
      <group position={[head.x, head.y, head.z]}>
        <mesh castShadow>
          <boxGeometry args={[1.35, 0.62, 0.4]} />
          <meshStandardMaterial color="#1c212b" metalness={0.45} roughness={0.4} />
        </mesh>
        {bulbRows.map((row) =>
          bulbOffsets.map((off) => (
            <mesh key={`bulb-${row}-${off}`} position={[off, row, 0.19]}>
              <boxGeometry args={[0.3, 0.22, 0.05]} />
              <meshStandardMaterial color="#fff3cf" emissive="#ffd98a" emissiveIntensity={4} toneMapped={false} />
            </mesh>
          )),
        )}
        <sprite scale={[7, 7, 1]}>
          <spriteMaterial
            map={glowTexture}
            color="#ffc46a"
            transparent
            opacity={0.85}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </sprite>
        {/* fake volumetric cones + dust */}
        <group quaternion={coneQuat}>
          <mesh geometry={coneGeometry}>
            <meshBasicMaterial
              color="#ffcf8a"
              transparent
              opacity={0.042}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
          <mesh geometry={innerConeGeometry}>
            <meshBasicMaterial
              color="#ffdca0"
              transparent
              opacity={0.028}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
              side={THREE.DoubleSide}
            />
          </mesh>
          <ConeDust length={coneLength} radius={coneRadius} />
        </group>
      </group>
    </group>
  );
}

function Crane() {
  const mastBase = useMemo(() => new THREE.Vector3(8.8, 0, 2.0), []);
  const mastTop = useMemo(() => new THREE.Vector3(8.8, 14.2, 2.0), []);
  const anchor = useMemo(() => new THREE.Vector3(CHAIN_ANCHOR.x, CHAIN_ANCHOR.y, CHAIN_ANCHOR.z), []);
  const armDir = useMemo(() => anchor.clone().sub(mastTop), [anchor, mastTop]);
  const armLength = armDir.length() + 0.8;
  const armQuat = useMemo(
    () => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(1, 0, 0), armDir.clone().normalize()),
    [armDir],
  );
  const armMid = useMemo(() => mastTop.clone().add(armDir.clone().multiplyScalar(0.5)), [mastTop, armDir]);

  const steel = { color: '#2c2f36', metalness: 0.55, roughness: 0.42 };
  const mastSegments = 11;
  const segH = mastTop.y / mastSegments;

  return (
    <group>
      {/* treads: two long dark boxes with wheel hints */}
      {[-1.05, 1.05].map((off) => (
        <group key={`tread-${off}`} position={[mastBase.x, 0, mastBase.z + off]}>
          <mesh position={[0, 0.36, 0]} castShadow receiveShadow>
            <boxGeometry args={[3.4, 0.72, 0.8]} />
            <meshStandardMaterial color="#191c21" metalness={0.4} roughness={0.6} />
          </mesh>
          {[-1.2, -0.4, 0.4, 1.2].map((wx) => (
            <mesh key={wx} position={[wx, 0.3, 0]} rotation={[Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[0.26, 0.26, 0.86, 10]} />
              <meshStandardMaterial color="#0e1013" metalness={0.5} roughness={0.5} />
            </mesh>
          ))}
        </group>
      ))}
      <mesh position={[mastBase.x, 0.95, mastBase.z]} castShadow>
        <boxGeometry args={[2.7, 0.5, 2.3]} />
        <meshStandardMaterial {...steel} />
      </mesh>
      {/* yellow-accent cab with two warm emissive windows */}
      <group position={[mastBase.x - 0.4, 1.95, mastBase.z - 0.2]}>
        <mesh castShadow>
          <boxGeometry args={[1.7, 1.5, 1.55]} />
          <meshStandardMaterial color="#c9a23f" metalness={0.25} roughness={0.55} />
        </mesh>
        <mesh position={[0, -0.35, 0]} castShadow>
          <boxGeometry args={[1.74, 0.42, 1.59]} />
          <meshStandardMaterial color="#2c2f36" metalness={0.4} roughness={0.5} />
        </mesh>
        {[-0.28, 0.34].map((wz) => (
          <mesh key={wz} position={[-0.86, 0.28, wz]} rotation={[0, -Math.PI / 2, 0]}>
            <planeGeometry args={[0.5, 0.5]} />
            <meshStandardMaterial color="#ffdf9a" emissive="#ffc76a" emissiveIntensity={2.2} toneMapped={false} />
          </mesh>
        ))}
        <pointLight position={[-1.3, 0.2, 0]} intensity={6} distance={6} color="#ffb54d" />
        <mesh position={[0.65, 0.86, 0.62]}>
          <boxGeometry args={[0.12, 0.12, 0.12]} />
          <meshStandardMaterial color="#8dffb0" emissive="#39ff6a" emissiveIntensity={5} toneMapped={false} />
        </mesh>
      </group>
      {/* X-braced lattice mast */}
      {[-0.42, 0.42].flatMap((ox) =>
        [-0.42, 0.42].map((oz) => (
          <mesh key={`mast-${ox}-${oz}`} position={[mastBase.x + ox, mastTop.y / 2 + 0.6, mastBase.z + oz]} castShadow>
            <boxGeometry args={[0.13, mastTop.y - 1.2, 0.13]} />
            <meshStandardMaterial {...steel} />
          </mesh>
        )),
      )}
      {Array.from({ length: mastSegments }, (_, i) => (
        <group key={`xbrace-${i}`} position={[mastBase.x, 1.6 + i * segH, mastBase.z]}>
          {/* X pair on the front/back faces */}
          <mesh position={[0, 0, 0.42]} rotation={[0, 0, 0.72]}>
            <boxGeometry args={[1.25, 0.06, 0.06]} />
            <meshStandardMaterial {...steel} />
          </mesh>
          <mesh position={[0, 0, 0.42]} rotation={[0, 0, -0.72]}>
            <boxGeometry args={[1.25, 0.06, 0.06]} />
            <meshStandardMaterial {...steel} />
          </mesh>
          {/* X pair on the side faces */}
          <mesh position={[0.42, 0, 0]} rotation={[0.72, 0, 0]}>
            <boxGeometry args={[0.06, 0.06, 1.25]} />
            <meshStandardMaterial {...steel} />
          </mesh>
          <mesh position={[0.42, 0, 0]} rotation={[-0.72, 0, 0]}>
            <boxGeometry args={[0.06, 0.06, 1.25]} />
            <meshStandardMaterial {...steel} />
          </mesh>
        </group>
      ))}
      {/* mast status lights */}
      {[4.6, 9.2, 13.6].map((y) => (
        <mesh key={`status-${y}`} position={[mastBase.x - 0.46, y, mastBase.z - 0.46]}>
          <boxGeometry args={[0.11, 0.11, 0.11]} />
          <meshStandardMaterial color="#8dffb0" emissive="#39ff6a" emissiveIntensity={5.5} toneMapped={false} />
        </mesh>
      ))}
      {/* counterweight behind the mast top */}
      <mesh position={[mastBase.x + 1.5, mastTop.y - 1.2, mastBase.z + 0.4]} castShadow>
        <boxGeometry args={[1.5, 1.05, 1.05]} />
        <meshStandardMaterial color="#1b1e24" metalness={0.4} roughness={0.5} />
      </mesh>
      {/* X-braced boom angled over the tower */}
      <group position={armMid.toArray()} quaternion={armQuat}>
        {[-0.26, 0.26].flatMap((oy) =>
          [-0.26, 0.26].map((oz) => (
            <mesh key={`chord-${oy}-${oz}`} position={[0, oy, oz]} castShadow>
              <boxGeometry args={[armLength, 0.11, 0.11]} />
              <meshStandardMaterial {...steel} />
            </mesh>
          )),
        )}
        {Array.from({ length: 7 }, (_, i) => {
          const x = -armLength / 2 + 0.4 + i * ((armLength - 0.8) / 6);
          return (
            <group key={`boomx-${i}`} position={[x, 0, 0]}>
              <mesh position={[0, 0, 0.26]} rotation={[0, 0, 0.85]}>
                <boxGeometry args={[0.68, 0.055, 0.055]} />
                <meshStandardMaterial {...steel} />
              </mesh>
              <mesh position={[0, 0, -0.26]} rotation={[0, 0, -0.85]}>
                <boxGeometry args={[0.68, 0.055, 0.055]} />
                <meshStandardMaterial {...steel} />
              </mesh>
              <mesh position={[0, 0.26, 0]} rotation={[0, 0.85, 0]}>
                <boxGeometry args={[0.68, 0.055, 0.055]} />
                <meshStandardMaterial {...steel} />
              </mesh>
            </group>
          );
        })}
      </group>
      {/* boom-tip status light + swivel block above the chain */}
      <mesh position={[anchor.x, anchor.y + 0.34, anchor.z]} castShadow>
        <boxGeometry args={[0.42, 0.3, 0.42]} />
        <meshStandardMaterial color="#3a3f46" metalness={0.6} roughness={0.45} />
      </mesh>
      <mesh position={[anchor.x - 0.32, anchor.y + 0.42, anchor.z]}>
        <boxGeometry args={[0.11, 0.11, 0.11]} />
        <meshStandardMaterial color="#8dffb0" emissive="#39ff6a" emissiveIntensity={5.5} toneMapped={false} />
      </mesh>
    </group>
  );
}

// ---------- effects ----------

const PUFF_POOL = 24;
const EMBER_COUNT = 140;

interface PuffState {
  active: boolean;
  life: number;
  maxLife: number;
  strength: number;
}

function Effects({
  fxRef,
  glowTexture,
  onBoomVignette,
}: {
  fxRef: MutableRefObject<YardFx | null>;
  glowTexture: THREE.Texture;
  onBoomVignette: () => void;
}) {
  const puffRefs = useRef<Array<THREE.Sprite | null>>([]);
  const puffStates = useRef<PuffState[]>(
    Array.from({ length: PUFF_POOL }, () => ({ active: false, life: 0, maxLife: 0.5, strength: 1 })),
  );
  const flashRef = useRef<THREE.PointLight | null>(null);
  const ringRef = useRef<THREE.Mesh | null>(null);
  const embersRef = useRef<THREE.Points | null>(null);
  const explosionState = useRef({ active: false, life: 0, x: 0, y: 0, z: 0 });
  const emberData = useRef({
    positions: new Float32Array(EMBER_COUNT * 3),
    velocities: new Float32Array(EMBER_COUNT * 3),
  });

  useEffect(() => {
    fxRef.current = {
      impact(x, y, z, strength) {
        const states = puffStates.current;
        const index = states.findIndex((state) => !state.active);
        if (index < 0) return;
        const sprite = puffRefs.current[index];
        if (!sprite) return;
        states[index] = { active: true, life: 0, maxLife: 0.5, strength: Math.min(1, strength / 10) };
        sprite.position.set(x, y, z);
        sprite.visible = true;
        // Pure physics: no camera shake on impacts (dust puffs stay).
      },
      explosion(x, y, z) {
        onBoomVignette();
        explosionState.current = { active: true, life: 0, x, y, z };
        const { positions, velocities } = emberData.current;
        for (let i = 0; i < EMBER_COUNT; i += 1) {
          positions[i * 3] = x + (Math.random() - 0.5) * 0.6;
          positions[i * 3 + 1] = y + (Math.random() - 0.5) * 0.6;
          positions[i * 3 + 2] = z + (Math.random() - 0.5) * 0.6;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);
          const speed = 2.5 + Math.random() * 6;
          velocities[i * 3] = Math.sin(phi) * Math.cos(theta) * speed;
          velocities[i * 3 + 1] = Math.abs(Math.cos(phi)) * speed + 2.2;
          velocities[i * 3 + 2] = Math.sin(phi) * Math.sin(theta) * speed;
        }
        const points = embersRef.current;
        if (points) {
          const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
          (attr.array as Float32Array).set(positions);
          attr.needsUpdate = true;
        }
        // Pure physics: boom keeps vignette + embers, no camera shake.
      },
    };
    return () => {
      fxRef.current = null;
    };
  }, [fxRef, onBoomVignette]);

  useFrame((_, delta) => {
    const states = puffStates.current;
    for (let i = 0; i < states.length; i += 1) {
      const state = states[i];
      const sprite = puffRefs.current[i];
      if (!state.active || !sprite) continue;
      state.life += delta;
      const t = state.life / state.maxLife;
      if (t >= 1) {
        state.active = false;
        sprite.visible = false;
        continue;
      }
      const scale = 0.7 + t * 2.4;
      sprite.scale.set(scale, scale, 1);
      const material = sprite.material as THREE.SpriteMaterial;
      material.opacity = 0.5 * (1 - t) * (0.4 + state.strength * 0.6);
      sprite.position.y += delta * 0.5;
    }

    const explosion = explosionState.current;
    const flash = flashRef.current;
    const ring = ringRef.current;
    const points = embersRef.current;
    if (explosion.active) {
      explosion.life += delta;
      const t = explosion.life;
      if (flash) {
        flash.position.set(explosion.x, explosion.y, explosion.z);
        flash.intensity = t < 0.3 ? 340 * (1 - t / 0.3) : 0;
      }
      if (ring) {
        ring.visible = t < 0.5;
        ring.position.set(explosion.x, Math.max(0.1, explosion.y - 0.4), explosion.z);
        const scale = 1 + t * 26;
        ring.scale.set(scale, scale, scale);
        (ring.material as THREE.MeshBasicMaterial).opacity = Math.max(0, 0.75 * (1 - t / 0.5));
      }
      if (points) {
        points.visible = t < 1;
        const attr = points.geometry.getAttribute('position') as THREE.BufferAttribute;
        const array = attr.array as Float32Array;
        const { velocities } = emberData.current;
        for (let i = 0; i < EMBER_COUNT; i += 1) {
          velocities[i * 3 + 1] -= 7.5 * delta;
          array[i * 3] += velocities[i * 3] * delta;
          array[i * 3 + 1] += velocities[i * 3 + 1] * delta;
          array[i * 3 + 2] += velocities[i * 3 + 2] * delta;
        }
        attr.needsUpdate = true;
        (points.material as THREE.PointsMaterial).opacity = Math.max(0, 1 - t);
      }
      if (t >= 1) {
        explosion.active = false;
        if (flash) flash.intensity = 0;
        if (ring) ring.visible = false;
        if (points) points.visible = false;
      }
    }
  });

  return (
    <group>
      {Array.from({ length: PUFF_POOL }, (_, i) => (
        <sprite
          key={`puff-${i}`}
          ref={(sprite) => {
            puffRefs.current[i] = sprite;
          }}
          visible={false}
        >
          <spriteMaterial
            map={glowTexture}
            color="#ffca8a"
            transparent
            opacity={0}
            blending={THREE.AdditiveBlending}
            depthWrite={false}
          />
        </sprite>
      ))}
      <pointLight ref={flashRef} intensity={0} distance={16} decay={2} color="#ffb54d" />
      <mesh ref={ringRef} visible={false} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.44, 0.52, 48]} />
        <meshBasicMaterial
          color="#ffc27a"
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <points ref={embersRef} visible={false}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[new Float32Array(EMBER_COUNT * 3), 3]} />
        </bufferGeometry>
        <pointsMaterial
          color="#ff9a3d"
          size={0.09}
          transparent
          opacity={0}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
          sizeAttenuation
          toneMapped={false}
        />
      </points>
    </group>
  );
}

/** Passthrough group — camera shake disabled for pure-physics viewing. */
function ShakeRig({ children }: { shakeRef?: MutableRefObject<number>; children: React.ReactNode }) {
  return <group>{children}</group>;
}

// ---------- physics render layer ----------

interface YardDebug {
  stepMs: number;
  ball: { x: number; y: number; z: number };
  ballScreen: { x: number; y: number };
  brickSample: Array<{ x: number; y: number; z: number }>;
  caps: Record<string, boolean>;
}

interface SleepingCityProbe {
  profile: string;
  structure: YardStructure;
  bodyLimit: number;
  bodyCount: number;
  brickCount: number;
  awakeCount: number;
  awakeBrickCount: number;
  fpsRolling: number;
  stepMsRolling: number;
  stepMsLast: number;
  idleMs: number;
  activeMs: number;
  heatmapEnabled: boolean;
  slowMo: boolean;
  cascadeActive: boolean;
  /** Post-cascade blue-keeper latch (true until user physics interaction). */
  cascadeKeepBlue: boolean;
  everAwakeCount: number;
  /** Actual world.step substeps selected on the last advance (Phase 1a). */
  substepsUsed: number;
  /** Actual FixedStepper maxStepsPerFrame applied on the last advance (Phase 1b). */
  maxStepsUsed: number;
  /** Phase 2: still-dynamic cascade bricks (not retired static debris). */
  dynamicBrickCount: number;
  /** Phase 2: retired (static) cascade brick count. */
  retiredCount: number;
}

/** Per rendered frame while a DEV cascade benchmark is armed. */
interface SleepingCityBenchSample {
  wallMs: number;
  simTime: number;
  /** Per-frame physics stepMs (runtime.stepMs when steps ran; 0 when zero fixed steps). */
  stepMs: number;
  awakeBricks: number;
  awakeCount: number;
  rawFps: number;
  rollingFps: number;
  stepsThisFrame: number;
  /** Actual substeps used by advanceYardPhysics this frame. */
  substepsUsed: number;
  /** Actual maxStepsPerFrame budget applied this frame. */
  maxStepsUsed: number;
  /** Cumulative bricks that have been awake at least once (cascade coverage). */
  everAwakeCount: number;
  /** Phase 2: still-dynamic cascade bricks (not retired static debris). */
  dynamicBrickCount: number;
  /** Phase 2: retired (static) cascade brick count. */
  retiredCount: number;
}

type SleepingCityBenchStatus = 'idle' | 'recording' | 'complete';

/**
 * DEV-only cascade-window recorder (Phase 0 + Phase 1 budget fields + Phase 2 retirement).
 * Distinct from __sleepingCityProbe.
 * steppedFrameStepMs* percentiles are over frames with stepsThisFrame > 0 only.
 * substepsUsed / maxStepsUsed are sorted unique actual budgets observed during the window
 * (not finish-frame-only, not hardcoded policy constants).
 * Phase 2 retirement fields: whole-window min/peak over the frozen sample curve
 * (do not invent a whole-window peak dynamic count — all bricks start dynamic).
 */
interface SleepingCityBenchSummary {
  finishReason: 'cascade_inactive' | 'wall_timeout' | 'empty';
  sampleCount: number;
  wallDurationMs: number;
  simDuration: number;
  /** ΔsimTime / wallDurationSec; 0 if wall duration is 0. */
  timeDilation: number;
  /** p50 of per-frame stepMs on stepped frames; null if none. */
  steppedFrameStepMsP50: number | null;
  /** p95 of per-frame stepMs on stepped frames; null if none. */
  steppedFrameStepMsP95: number | null;
  /** max of per-frame stepMs on stepped frames; null if none. */
  steppedFrameStepMsMax: number | null;
  minFps: number | null;
  peakAwakeBricks: number;
  peakAwakeCount: number;
  peakStepsPerFrame: number;
  everAwakeCount: number;
  coverage: number;
  brickCount: number;
  /** runtime.stepper.fixedDt used for the capture. */
  fixedDt: number;
  /** Sorted unique actual substeps values used across recorded samples. */
  substepsUsed: number[];
  /** Sorted unique actual maxStepsPerFrame values used across recorded samples. */
  maxStepsUsed: number[];
  /**
   * Minimum dynamicBrickCount over the whole recorded cascade window.
   * 0 when no samples (empty). Not a post-settle peak — reviewers use the curve
   * for moving-window analysis (all bricks legitimately start dynamic).
   */
  minDynamicBrickCount: number;
  /** dynamicBrickCount on the final recorded sample; 0 when empty. */
  finalDynamicBrickCount: number;
  /** Maximum retiredCount over the whole recorded cascade window; 0 when empty. */
  peakRetiredCount: number;
  /** retiredCount on the final recorded sample; 0 when empty. */
  finalRetiredCount: number;
  /**
   * wallMs of the first sample with retiredCount > 0; null if none retired
   * during the window (or empty).
   */
  retirementStartWallMs: number | null;
}

interface SleepingCityBench {
  status: SleepingCityBenchStatus;
  sampleCount: number;
  summary: SleepingCityBenchSummary | null;
  /**
   * Frozen per-frame curve after complete; null while idle/recording
   * (do not publish a growing array each frame).
   */
  samples: SleepingCityBenchSample[] | null;
}

/** Wall-clock cap for cascade benchmark recording (ms). Documented finish path. */
const SLEEPING_CITY_BENCH_WALL_TIMEOUT_MS = 120_000;

function emptyBenchSummary(partial?: Partial<SleepingCityBenchSummary>): SleepingCityBenchSummary {
  return {
    finishReason: 'empty',
    sampleCount: 0,
    wallDurationMs: 0,
    simDuration: 0,
    timeDilation: 0,
    steppedFrameStepMsP50: null,
    steppedFrameStepMsP95: null,
    steppedFrameStepMsMax: null,
    minFps: null,
    peakAwakeBricks: 0,
    peakAwakeCount: 0,
    peakStepsPerFrame: 0,
    everAwakeCount: 0,
    coverage: 0,
    brickCount: 0,
    fixedDt: 1 / 60,
    substepsUsed: [],
    maxStepsUsed: [],
    minDynamicBrickCount: 0,
    finalDynamicBrickCount: 0,
    peakRetiredCount: 0,
    finalRetiredCount: 0,
    retirementStartWallMs: null,
    ...partial,
  };
}

/** Sorted unique numeric values observed during a bench window. */
function sortedUniqueNumbers(values: Iterable<number>): number[] {
  return Array.from(new Set(values)).sort((a, b) => a - b);
}

/** Nearest-rank percentile on a pre-sorted ascending array. */
function percentileSorted(sorted: number[], p: number): number | null {
  if (sorted.length === 0) return null;
  if (sorted.length === 1) return sorted[0];
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[idx];
}

function createIdleSleepingCityBench(): SleepingCityBench {
  return { status: 'idle', sampleCount: 0, summary: null, samples: null };
}

/**
 * Cascade sleep lifecycle (camera-independent). Tracks wake-wave settle
 * choreography after CASCADE; OrbitControls stay fully user-owned.
 */
interface CascadeLifecycle {
  /** True from cascadeLaunch until rubble is re-slept (probe cascadeActive). */
  active: boolean;
  /** Sim-time clock (sum of capped frame dt while active). */
  elapsed: number;
  /** performance.now() at cascade launch (wall-clock safety net for headless under-sim). */
  wallStartedAt: number;
  /** Whether the wrecking ball has been calmed after the initial strike window. */
  ballCalmed: boolean;
  /** Elapsed sim-time when ever-awake coverage first crossed the settle threshold (-1 = not yet). */
  coverageHitAt: number;
  /** performance.now() when coverage first crossed threshold (-1 = not yet). */
  coverageHitWallAt: number;
  /** Whether we've completed the guarded rubble-settle phase after the wave. */
  rubbleSlept: boolean;
  /**
   * Post-cascade blue-keeper: residual ground contacts get another soft settle pass.
   * Cleared on the next user physics interaction so tools work again.
   */
  keepBlue: boolean;
}

function createCascadeLifecycle(): CascadeLifecycle {
  return {
    active: false,
    elapsed: 0,
    wallStartedAt: 0,
    ballCalmed: false,
    coverageHitAt: -1,
    coverageHitWallAt: -1,
    rubbleSlept: false,
    keepBlue: false,
  };
}

function clearCascadeLifecycle(life: CascadeLifecycle) {
  life.active = false;
  life.elapsed = 0;
  life.wallStartedAt = 0;
  life.ballCalmed = false;
  life.coverageHitAt = -1;
  life.coverageHitWallAt = -1;
  life.rubbleSlept = false;
  life.keepBlue = false;
}

function finishCascadeRubbleSleep(
  life: CascadeLifecycle,
  runtime: YardRuntime,
  worker: YardWorkerClient | null,
) {
  if (worker) {
    worker.calmWreckingBall();
    worker.forceSleepAllCascadeBricks(false);
  } else {
    calmWreckingBall(runtime);
    forceSleepAllCascadeBricks(runtime, { hard: false });
  }
  life.rubbleSlept = true;
  life.active = false;
  life.keepBlue = true;
}

/** User touched physics again — stop the post-cascade soft-settle pass. */
function releaseCascadeBlueKeeper(life: CascadeLifecycle) {
  life.keepBlue = false;
}

function YardPhysicsLayer({
  toolRef,
  structure,
  initialStructure,
  bodyLimit,
  slowMo,
  heatmapEnabled,
  resetToken,
  cascadeToken,
  statsRef,
  fxRef,
  controlsRef,
  onReady,
}: {
  // Ref, not state: the tool must be current the instant a pointerdown lands,
  // even if it arrives before React re-renders (fast tool-click → canvas-click).
  toolRef: MutableRefObject<YardTool>;
  structure: YardStructure;
  initialStructure: YardStructure;
  bodyLimit: number;
  slowMo: boolean;
  heatmapEnabled: boolean;
  resetToken: number;
  cascadeToken: number;
  statsRef: MutableRefObject<YardStats>;
  fxRef: MutableRefObject<YardFx | null>;
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  onReady: () => void;
}) {
  const { gl, camera } = useThree();
  const runtimeRef = useRef<YardRuntime | null>(null);
  /** Phase 3: Sleeping City worker client; null on main-thread yard path. */
  const workerRef = useRef<YardWorkerClient | null>(null);
  const useWorker = shouldUseYardPhysicsWorker(initialStructure, bodyLimit);
  const brickMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const cannonMeshRef = useRef<THREE.InstancedMesh | null>(null);
  const ballMeshRef = useRef<THREE.Mesh | null>(null);
  const chainRefs = useRef<Array<THREE.Mesh | null>>([]);
  const colorsVersionRef = useRef(-1);
  const colorModeRef = useRef<'base' | 'heatmap' | null>(null);
  /** Worker path: last snapshot sequence whose instance matrices were written. */
  const lastMatrixSnapshotSeqRef = useRef(-1);
  const dragStateRef = useRef<{ mode: 'none' | 'ball' | 'brick'; planeY: number }>({ mode: 'none', planeY: 0 });
  const lastCascadeToken = useRef(cascadeToken);
  const cascadeLifeRef = useRef<CascadeLifecycle>(createCascadeLifecycle());
  const postToolSettleSweepRef = useRef(0);
  /** Brief reduced-solver window after cannon/boom; full quality returns for settling. */
  const postToolOptimizationRef = useRef(0);
  /** DEV-only cascade benchmark internal state; no sample work when status !== 'recording'. */
  const benchRef = useRef<{
    public: SleepingCityBench;
    wallStartedAt: number;
    simStartedAt: number;
    samples: SleepingCityBenchSample[];
    steppedStepMs: number[];
    substepsSeen: number[];
    maxStepsSeen: number[];
    minFps: number;
    peakAwakeBricks: number;
    peakAwakeCount: number;
    peakStepsPerFrame: number;
    /** Running min of sample dynamicBrickCount; Infinity until first sample. */
    minDynamicBrickCount: number;
    peakRetiredCount: number;
    /** wallMs of first sample with retiredCount > 0; null until then. */
    retirementStartWallMs: number | null;
  }>({
    public: createIdleSleepingCityBench(),
    wallStartedAt: 0,
    simStartedAt: 0,
    samples: [],
    steppedStepMs: [],
    substepsSeen: [],
    maxStepsSeen: [],
    minFps: Infinity,
    peakAwakeBricks: 0,
    peakAwakeCount: 0,
    peakStepsPerFrame: 0,
    minDynamicBrickCount: Infinity,
    peakRetiredCount: 0,
    retirementStartWallMs: null,
  });
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const color = useMemo(() => new THREE.Color(), []);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const pointerNdc = useMemo(() => new THREE.Vector2(), []);
  const planeHit = useMemo(() => new THREE.Vector3(), []);
  const dragPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  const groundPlane = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);

  // runtime bootstrap — Sleeping City scale cascade uses transferable-AB worker
  useEffect(() => {
    let cancelled = false;
    if (useWorker) {
      const client = new YardWorkerClient();
      workerRef.current = client;
      client
        .start(initialStructure, bodyLimit)
        .then((runtime) => {
          if (cancelled) {
            client.dispose();
            return;
          }
          runtimeRef.current = runtime;
          writeYardState({
            physicsReady: true,
            structure: runtime.structure,
            brickCount: runtime.brickBodies.length,
            bodyCount: runtime.bodyCountTotal,
            awakeCount: runtime.awakeCount,
          });
          onReady();
        })
        .catch((error: unknown) => {
          console.error('Yard physics worker failed to initialize', error);
          writeYardState({ physicsReady: false });
        });
      return () => {
        cancelled = true;
        workerRef.current = null;
        runtimeRef.current = null;
        client.dispose();
      };
    }

    getYardRuntime(initialStructure, { bodyLimit })
      .then((runtime) => {
        if (cancelled) return;
        runtimeRef.current = runtime;
        writeYardState({
          physicsReady: true,
          structure: runtime.structure,
          brickCount: runtime.brickBodies.length,
          bodyCount: runtime.bodyCountTotal,
          awakeCount: runtime.awakeCount,
        });
        onReady();
      })
      .catch((error: unknown) => {
        console.error('Yard physics failed to initialize', error);
        writeYardState({ physicsReady: false });
      });
    return () => {
      cancelled = true;
    };
  }, [bodyLimit, initialStructure, onReady, useWorker]);

  // structure / slow-mo / reset plumbed into the runtime
  useEffect(() => {
    const runtime = runtimeRef.current;
    const worker = workerRef.current;
    if (runtime && runtime.structure !== structure) {
      if (worker) worker.setStructure(structure);
      else setYardStructure(runtime, structure);
      clearCascadeLifecycle(cascadeLifeRef.current);
      postToolOptimizationRef.current = 0;
    }
    writeYardState({ structure });
  }, [structure]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    const worker = workerRef.current;
    if (worker) worker.setSlowMo(slowMo);
    else if (runtime) runtime.slowMo = slowMo;
  }, [slowMo]);

  const lastResetToken = useRef(resetToken);
  useEffect(() => {
    const runtime = runtimeRef.current;
    const worker = workerRef.current;
    if (runtime && resetToken !== lastResetToken.current) {
      lastResetToken.current = resetToken;
      if (worker) worker.reset();
      else resetYard(runtime);
      clearCascadeLifecycle(cascadeLifeRef.current);
      postToolOptimizationRef.current = 0;
      const controls = controlsRef.current;
      if (controls) controls.enabled = true;
    }
  }, [resetToken, controlsRef]);

  // CASCADE button → wrecking-ball strike + sleep-lifecycle tracker (camera stays free).
  useEffect(() => {
    const runtime = runtimeRef.current;
    const worker = workerRef.current;
    if (!runtime || cascadeToken === lastCascadeToken.current) return;
    lastCascadeToken.current = cascadeToken;
    if (cascadeToken === 0) return;

    if (worker) worker.cascadeLaunch();
    else cascadeLaunch(runtime);

    const life = cascadeLifeRef.current;
    life.active = true;
    life.elapsed = 0;
    life.wallStartedAt = performance.now();
    life.ballCalmed = false;
    life.coverageHitAt = -1;
    life.coverageHitWallAt = -1;
    life.rubbleSlept = false;
    life.keepBlue = false;
    postToolOptimizationRef.current = 0;

    // Arm DEV cascade benchmark exactly on a new CASCADE launch.
    if (import.meta.env.DEV) {
      const b = benchRef.current;
      b.wallStartedAt = performance.now();
      b.simStartedAt = runtime.stepper.simTime;
      b.samples = [];
      b.steppedStepMs = [];
      b.substepsSeen = [];
      b.maxStepsSeen = [];
      b.minFps = Infinity;
      b.peakAwakeBricks = 0;
      b.peakAwakeCount = 0;
      b.peakStepsPerFrame = 0;
      b.minDynamicBrickCount = Infinity;
      b.peakRetiredCount = 0;
      b.retirementStartWallMs = null;
      b.public = { status: 'recording', sampleCount: 0, summary: null, samples: null };
    }
  }, [cascadeToken]);

  // pointer interactions
  useEffect(() => {
    const canvas = gl.domElement;

    const toNdc = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      pointerNdc.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointerNdc, camera);
    };

    const intersectDragPlane = () => raycaster.ray.intersectPlane(dragPlane, planeHit);

    const clampBallTarget = (point: THREE.Vector3) => {
      const dx = point.x - CHAIN_ANCHOR.x;
      const dz = point.z - CHAIN_ANCHOR.z;
      const reach = 4.5;
      const d = Math.hypot(dx, dz);
      if (d > reach) {
        point.x = CHAIN_ANCHOR.x + (dx / d) * reach;
        point.z = CHAIN_ANCHOR.z + (dz / d) * reach;
      }
      point.y = Math.min(Math.max(point.y, 0.7), 9.5);
      return point;
    };

    const onPointerMove = (event: PointerEvent) => {
      const runtime = runtimeRef.current;
      const worker = workerRef.current;
      const drag = dragStateRef.current;
      if (!runtime || drag.mode === 'none') return;
      toNdc(event);
      if (!intersectDragPlane()) return;
      const target = planeHit.clone();
      if (drag.mode === 'ball') {
        clampBallTarget(target);
        if (worker) worker.updateDrag(target.x, target.y, target.z);
        else updateDragTarget(runtime, target.x, target.y, target.z);
      } else {
        const y = Math.min(Math.max(target.y + 0.5, 0.6), 10);
        if (worker) worker.updateDrag(target.x, y, target.z);
        else updateDragTarget(runtime, target.x, y, target.z);
      }
    };

    const stopDrag = () => {
      const runtime = runtimeRef.current;
      const worker = workerRef.current;
      if (worker) worker.endDrag();
      else if (runtime) endDrag(runtime);
      dragStateRef.current = { mode: 'none', planeY: 0 };
      const controls = controlsRef.current;
      if (controls) controls.enabled = true;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDrag);
    };

    const startDragListeners = () => {
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDrag);
    };

    const onPointerDown = (event: PointerEvent) => {
      const runtime = runtimeRef.current;
      const worker = workerRef.current;
      if (!runtime) return;
      const activeTool = toolRef.current;
      // Cannon fires on the right mouse button so the left button stays free
      // for OrbitControls rotate/pan while aiming. Every other tool keeps the
      // left button.
      const requiredButton = activeTool === 'cannon' ? 2 : 0;
      if (event.button !== requiredButton) return;

      toNdc(event);
      const brickMesh = brickMeshRef.current;

      if (activeTool === 'ball') {
        const ball = ballPosition(runtime);
        const center = new THREE.Vector3(ball.x, ball.y, ball.z);
        if (raycaster.ray.distanceSqToPoint(center) < (BALL_RADIUS + 1.0) ** 2) {
          // Physics interaction — release post-cascade blue-keeper.
          releaseCascadeBlueKeeper(cascadeLifeRef.current);
          dragPlane.set(new THREE.Vector3(0, 1, 0), -ball.y);
          dragStateRef.current = { mode: 'ball', planeY: ball.y };
          if (worker) worker.beginDrag(runtime.ballBodyId, ball.x, ball.y, ball.z);
          else beginDrag(runtime, runtime.ballBodyId, ball.x, ball.y, ball.z);
          const controls = controlsRef.current;
          if (controls) controls.enabled = false;
          startDragListeners();
        }
        return;
      }

      if (activeTool === 'cannon') {
        releaseCascadeBlueKeeper(cascadeLifeRef.current);
        // Coverage is sticky after cascade, so this enables the reduced {1,1}
        // overload budget only for the expensive impact window. It expires
        // before the normal 4-substep re-settle phase.
        postToolOptimizationRef.current = 3;
        const dir = raycaster.ray.direction.clone().normalize();
        // Honest ballistic shot: spawn near the camera so the full flight is visible.
        // High CANNONBALL_SPEED keeps gravity drop small over city-range distances.
        const origin = (camera.position as THREE.Vector3).clone().addScaledVector(dir, 2.2);
        if (import.meta.env.DEV) {
          if (worker) worker.resetChainStability();
          else resetChainStability(runtime);
        }
        if (worker) {
          worker.spawnCannon(
            { x: origin.x, y: origin.y, z: origin.z },
            { x: dir.x, y: dir.y, z: dir.z },
          );
        } else {
          spawnCannonball(runtime, { x: origin.x, y: origin.y, z: origin.z }, { x: dir.x, y: dir.y, z: dir.z });
        }
        return;
      }

      if (activeTool === 'boom') {
        let point: THREE.Vector3 | null = null;
        if (brickMesh) {
          const hits = raycaster.intersectObject(brickMesh, false);
          if (hits.length > 0) point = hits[0].point.clone();
        }
        if (!point && raycaster.ray.intersectPlane(groundPlane, planeHit)) {
          point = planeHit.clone();
        }
        if (point) {
          releaseCascadeBlueKeeper(cascadeLifeRef.current);
          postToolOptimizationRef.current = 3;
          point.x = Math.min(Math.max(point.x, -30), 30);
          point.z = Math.min(Math.max(point.z, -30), 30);
          if (worker) worker.boom(point.x, point.y, point.z);
          else boomAt(runtime, point.x, point.y, point.z);
          fxRef.current?.explosion(point.x, point.y, point.z);
        }
        return;
      }

      if (activeTool === 'grab' && brickMesh) {
        const hits = raycaster.intersectObject(brickMesh, false);
        const instanceId = hits[0]?.instanceId;
        if (instanceId == null) return;
        const bodyId = brickBodyIdAt(runtime, instanceId);
        if (!bodyId) return;
        releaseCascadeBlueKeeper(cascadeLifeRef.current);
        const offset = instanceId * 7;
        const bx = runtime.transforms[offset];
        const by = runtime.transforms[offset + 1];
        const bz = runtime.transforms[offset + 2];
        dragPlane.set(new THREE.Vector3(0, 1, 0), -by);
        dragStateRef.current = { mode: 'brick', planeY: by };
        if (worker) worker.beginDrag(bodyId, bx, by, bz);
        else beginDrag(runtime, bodyId, bx, by, bz);
        const controls = controlsRef.current;
        if (controls) controls.enabled = false;
        startDragListeners();
      }
    };

    // Right-click fires the cannon (see onPointerDown) — suppress the browser's
    // native context menu on the canvas so it doesn't pop up on fire.
    const onContextMenu = (event: MouseEvent) => event.preventDefault();

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('contextmenu', onContextMenu);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('contextmenu', onContextMenu);
      stopDrag();
    };
  }, [camera, controlsRef, dragPlane, fxRef, gl, groundPlane, planeHit, pointerNdc, raycaster, toolRef]);

  useFrame((state, delta) => {
    const runtime = runtimeRef.current;
    const worker = workerRef.current;
    writeYardState({ canvasReady: true });
    if (!runtime) return;

    const postToolOptimizationActive = postToolOptimizationRef.current > 0;
    const cascadeOptimizationActive =
      cascadeLifeRef.current.active || postToolOptimizationActive;

    // Worker path: coalesce dt + drain snapshot; main never world.step.
    const stepsThisFrame = worker
      ? worker.advance(delta, cascadeOptimizationActive)
      : advanceYardPhysics(runtime, delta, cascadeOptimizationActive);
    if (postToolOptimizationActive) {
      postToolOptimizationRef.current = Math.max(
        0,
        postToolOptimizationRef.current - Math.min(delta, 0.1),
      );
    }

    const stats = statsRef.current;
    let awakeBricks = 0;
    for (let i = 0; i < runtime.brickSleepStates.length; i += 1) {
      if (runtime.brickSleepStates[i] !== 1) continue;
      awakeBricks += 1;
    }
    stats.bodies = runtime.bodyCountTotal;
    stats.bricks = runtime.brickBodies.length;
    stats.awake = runtime.awakeCount;
    stats.awakeBricks = awakeBricks;
    stats.stepMs = stats.stepMs * 0.85 + runtime.stepMs * 0.15;
    if (awakeBricks === 0) {
      stats.idleMs = stats.idleMs * 0.85 + runtime.stepMs * 0.15;
    } else {
      stats.activeMs = Math.max(stats.activeMs * 0.996, runtime.stepMs);
    }
    stats.fps = delta > 0 ? stats.fps * 0.92 + (1 / delta) * 0.08 : stats.fps;

    writeYardState({
      bodyCount: runtime.bodyCountTotal,
      brickCount: runtime.brickBodies.length,
      awakeCount: runtime.awakeCount,
      everAwakeCount: runtime.everAwakeCount,
    });

    // ---- cascade sleep lifecycle (camera stays under OrbitControls) ----
    const life = cascadeLifeRef.current;
    const dt = Math.min(delta, 0.05);
    if (life.active) {
      life.elapsed += dt;

      // After the wave has had time to propagate, quiet the ball so residual
      // pendulum swing doesn't keep re-waking settled rubble.
      if (!life.ballCalmed && life.elapsed > 14) {
        if (worker) worker.calmWreckingBall();
        else calmWreckingBall(runtime);
        life.ballCalmed = true;
      }

      const coverage = runtime.brickBodies.length > 0 ? runtime.everAwakeCount / runtime.brickBodies.length : 0;
      if (coverage >= 0.75 && life.coverageHitAt < 0) {
        life.coverageHitAt = life.elapsed;
        life.coverageHitWallAt = performance.now();
      }

      // Soft-settle only. A global hard sleep can pin unsupported tower sections
      // in mid-air; readiness gates end lifecycle bookkeeping, not gravity.
      const postCoverage = life.coverageHitAt >= 0 ? life.elapsed - life.coverageHitAt : -1;
      const postCoverageWallSec =
        life.coverageHitWallAt >= 0 ? (performance.now() - life.coverageHitWallAt) / 1000 : -1;
      if (!life.rubbleSlept && postCoverage >= 0) {
        if (postCoverage > 3) {
          if (worker) worker.sleepSettledCascadeBricks();
          else sleepSettledCascadeBricks(runtime);
        }
        const airborne = countAirborneMovingCascadeBricks(runtime);
        const ready = cascadeRubbleReadyToSleep(runtime) || airborne <= 20;
        if (postCoverage > 6) {
          if (worker) {
            worker.calmWreckingBall();
            worker.forceSleepAllCascadeBricks(false);
          } else {
            calmWreckingBall(runtime);
            forceSleepAllCascadeBricks(runtime, { hard: false });
          }
        }
        // Primary: ready-gated once rubble has had time to land (~8s sim post).
        // Secondary: looser airborne gate a bit later (micro-jitter may hold a few
        // "moving" forever). Absolute: ~20s sim OR ~28s wall after coverage so
        // headless under-sim still finishes (e2e extends poll budget, not early freeze).
        if (
          (postCoverage > 8 && ready) ||
          (postCoverage > 14 && airborne <= 80) ||
          postCoverage > 20 ||
          postCoverageWallSec > 28
        ) {
          finishCascadeRubbleSleep(life, runtime, worker);
        }
      } else if (!life.rubbleSlept && (life.elapsed > 55 || performance.now() - life.wallStartedAt > 90000)) {
        // Coverage never crossed 0.75 (or sim stalled): still end the lifecycle.
        if (worker) worker.sleepSettledCascadeBricks();
        else sleepSettledCascadeBricks(runtime);
        finishCascadeRubbleSleep(life, runtime, worker);
      }
    } else if (runtime.structure === 'cascade' && life.keepBlue && awakeBricks > 0) {
      // Pre-interaction blue-keeper only: re-sleep grounded, per-step-still
      // rubble. Elevated bodies remain governed by gravity and normal sleep.
      // Cleared on the next tool pointerdown so cannon/boom/grab work again.
      // Re-calm the ball each frame — residual pendulum is the usual re-wake source.
      if (worker) {
        worker.calmWreckingBall();
        worker.forceSleepAllCascadeBricks(false);
      } else {
        calmWreckingBall(runtime);
        forceSleepAllCascadeBricks(runtime, { hard: false });
      }
      postToolSettleSweepRef.current = 0;
    } else if (
      runtime.structure === 'cascade' &&
      awakeBricks > 0 &&
      dragStateRef.current.mode === 'none'
    ) {
      // Once a post-cascade tool has had three seconds to play out, periodically
      // help only grounded, per-step-still rubble return to sleep. This preserves
      // sustained cannon/boom motion while preventing a small contact-jitter tail
      // from remaining awake indefinitely.
      postToolSettleSweepRef.current += dt;
      if (postToolSettleSweepRef.current >= 3) {
        postToolSettleSweepRef.current = 0;
        if (worker) worker.sleepSettledCascadeBricks();
        else sleepSettledCascadeBricks(runtime);
      }
    } else {
      postToolSettleSweepRef.current = 0;
    }

    // impacts → dust puffs
    if (runtime.impacts.length > 0) {
      const fx = fxRef.current;
      if (fx) {
        for (const impact of runtime.impacts) {
          fx.impact(impact.x, impact.y, impact.z, impact.strength);
        }
      }
      runtime.impacts.length = 0;
    }

    const brickMesh = brickMeshRef.current;
    const cannonMesh = cannonMeshRef.current;
    const ballMesh = ballMeshRef.current;
    const appliedSeq = worker ? worker.latestAppliedSequence : -1;
    const workerSnapshotChanged =
      worker != null && appliedSeq !== lastMatrixSnapshotSeqRef.current;

    // Colors/heatmap stay on every relevant frame (structure change, mode toggle,
    // live sleep heatmap). Matrix writes are independent — see below.
    if (brickMesh) {
      const nextColorMode = heatmapEnabled ? 'heatmap' : 'base';
      const structureChanged = colorsVersionRef.current !== runtime.structureVersion;
      const colorModeChanged = colorModeRef.current !== nextColorMode;
      if (
        structureChanged ||
        colorModeChanged ||
        (heatmapEnabled && (!worker || workerSnapshotChanged))
      ) {
        colorsVersionRef.current = runtime.structureVersion;
        colorModeRef.current = nextColorMode;
        brickMesh.count = runtime.brickBodies.length;
        // InstancedMesh.raycast() uses a cached whole-mesh bounding sphere that
        // three computes once (when count could still be 0) — give it a static
        // generous sphere so click raycasts against bricks always get past the
        // broadphase, wherever the bricks have flown.
        brickMesh.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 5, 0), 90);
        for (let i = 0; i < runtime.brickBodies.length; i += 1) {
          const jitter = seedRand(i * 31 + 7);
          if (heatmapEnabled) {
            color.set(getSleepHeatmapColor(runtime.brickSleepStates[i] === 1));
            color.offsetHSL(0, (jitter - 0.5) * 0.04, (jitter - 0.5) * 0.12);
          } else {
            color.set(runtime.brickColors[i]);
            color.offsetHSL(0, (jitter - 0.5) * 0.05, (jitter - 0.5) * 0.13);
          }
          brickMesh.setColorAt(i, color);
        }
        if (brickMesh.instanceColor) brickMesh.instanceColor.needsUpdate = true;
      }
    }

    // Worker path: rewrite brick/chain/ball/cannon matrices only once per newly
    // applied snapshot (main-thread path still updates every R3F frame).
    const matricesDirty = !worker || workerSnapshotChanged;
    if (matricesDirty) {
      if (worker) lastMatrixSnapshotSeqRef.current = appliedSeq;

      if (brickMesh) {
        for (let i = 0; i < runtime.brickBodies.length; i += 1) {
          const offset = i * 7;
          dummy.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
          dummy.quaternion.set(
            runtime.transforms[offset + 3],
            runtime.transforms[offset + 4],
            runtime.transforms[offset + 5],
            runtime.transforms[offset + 6],
          );
          dummy.scale.setScalar(1);
          dummy.updateMatrix();
          brickMesh.setMatrixAt(i, dummy.matrix);
        }
        brickMesh.instanceMatrix.needsUpdate = true;
      }

      for (let link = 0; link < CHAIN_LINKS; link += 1) {
        const mesh = chainRefs.current[link];
        if (!mesh) continue;
        const offset = chainTransformOffset(runtime, link);
        mesh.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
        mesh.quaternion.set(
          runtime.transforms[offset + 3],
          runtime.transforms[offset + 4],
          runtime.transforms[offset + 5],
          runtime.transforms[offset + 6],
        );
      }

      if (ballMesh) {
        const offset = ballTransformOffset(runtime);
        ballMesh.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
        ballMesh.quaternion.set(
          runtime.transforms[offset + 3],
          runtime.transforms[offset + 4],
          runtime.transforms[offset + 5],
          runtime.transforms[offset + 6],
        );
      }

      if (cannonMesh) {
        cannonMesh.count = MAX_CANNONBALLS;
        for (let i = 0; i < MAX_CANNONBALLS; i += 1) {
          if (i < runtime.cannonballs.length) {
            const offset = cannonballTransformOffset(runtime, i);
            dummy.position.set(runtime.transforms[offset], runtime.transforms[offset + 1], runtime.transforms[offset + 2]);
            dummy.quaternion.set(
              runtime.transforms[offset + 3],
              runtime.transforms[offset + 4],
              runtime.transforms[offset + 5],
              runtime.transforms[offset + 6],
            );
            dummy.scale.setScalar(1);
          } else {
            dummy.position.set(0, -120, 0);
            dummy.quaternion.identity();
            dummy.scale.setScalar(0.001);
          }
          dummy.updateMatrix();
          cannonMesh.setMatrixAt(i, dummy.matrix);
        }
        cannonMesh.instanceMatrix.needsUpdate = true;
      }
    }

    if (import.meta.env.DEV) {
      const ball = ballPosition(runtime);
      const screen = new THREE.Vector3(ball.x, ball.y, ball.z).project(state.camera);
      const rect = gl.domElement.getBoundingClientRect();
      const brickSample: Array<{ x: number; y: number; z: number }> = [];
      for (let i = 0; i < Math.min(10, runtime.brickBodies.length); i += 1) {
        const offset = i * 7;
        brickSample.push({
          x: runtime.transforms[offset],
          y: runtime.transforms[offset + 1],
          z: runtime.transforms[offset + 2],
        });
      }
      const debugWindow = window as unknown as Record<string, YardDebug>;
      debugWindow[`__yard${'Debug'}`] = {
        stepMs: runtime.stepMs,
        ball,
        ballScreen: {
          x: rect.left + ((screen.x + 1) / 2) * rect.width,
          y: rect.top + ((1 - screen.y) / 2) * rect.height,
        },
        brickSample,
        caps: { ...runtime.caps },
      };
      const chain = getChainStability(runtime);
      (window as unknown as { __sleepingCityProbe?: SleepingCityProbe }).__sleepingCityProbe = {
        profile: runtime.profileKey,
        structure: runtime.structure,
        bodyLimit,
        bodyCount: runtime.bodyCountTotal,
        brickCount: runtime.brickBodies.length,
        awakeCount: runtime.awakeCount,
        awakeBrickCount: stats.awakeBricks,
        fpsRolling: stats.fps,
        stepMsRolling: stats.stepMs,
        stepMsLast: runtime.stepMs,
        idleMs: stats.idleMs,
        activeMs: stats.activeMs,
        heatmapEnabled,
        slowMo: runtime.slowMo,
        // True while cascade is running and rubble has not yet been re-slept.
        cascadeActive: cascadeLifeRef.current.active,
        cascadeKeepBlue: cascadeLifeRef.current.keepBlue,
        everAwakeCount: runtime.everAwakeCount,
        substepsUsed: runtime.substepsUsed,
        maxStepsUsed: runtime.maxStepsUsed,
        dynamicBrickCount: runtime.dynamicBrickCount,
        retiredCount: runtime.retiredCount,
      };
      (window as unknown as { __chainStability?: typeof chain }).__chainStability = chain;

      // ---- DEV cascade benchmark recorder (no sample alloc outside recording) ----
      const bench = benchRef.current;
      if (bench.public.status === 'recording') {
        const wallNow = performance.now();
        const wallMs = wallNow - bench.wallStartedAt;
        const frameStepMs = stepsThisFrame > 0 ? runtime.stepMs : 0;
        const rawFps = delta > 0 ? 1 / delta : 0;
        const frameSubsteps = runtime.substepsUsed;
        const frameMaxSteps = runtime.maxStepsUsed;
        const frameDynamicBricks = runtime.dynamicBrickCount;
        const frameRetired = runtime.retiredCount;
        bench.samples.push({
          wallMs,
          simTime: runtime.stepper.simTime,
          stepMs: frameStepMs,
          awakeBricks,
          awakeCount: runtime.awakeCount,
          rawFps,
          rollingFps: stats.fps,
          stepsThisFrame,
          substepsUsed: frameSubsteps,
          maxStepsUsed: frameMaxSteps,
          everAwakeCount: runtime.everAwakeCount,
          dynamicBrickCount: frameDynamicBricks,
          retiredCount: frameRetired,
        });
        bench.substepsSeen.push(frameSubsteps);
        bench.maxStepsSeen.push(frameMaxSteps);
        if (stepsThisFrame > 0) bench.steppedStepMs.push(frameStepMs);
        if (rawFps > 0 && rawFps < bench.minFps) bench.minFps = rawFps;
        if (awakeBricks > bench.peakAwakeBricks) bench.peakAwakeBricks = awakeBricks;
        if (runtime.awakeCount > bench.peakAwakeCount) bench.peakAwakeCount = runtime.awakeCount;
        if (stepsThisFrame > bench.peakStepsPerFrame) bench.peakStepsPerFrame = stepsThisFrame;
        if (frameDynamicBricks < bench.minDynamicBrickCount) bench.minDynamicBrickCount = frameDynamicBricks;
        if (frameRetired > bench.peakRetiredCount) bench.peakRetiredCount = frameRetired;
        if (bench.retirementStartWallMs === null && frameRetired > 0) {
          bench.retirementStartWallMs = wallMs;
        }
        // Keep public.samples null while recording (no growing array each frame).
        bench.public = {
          status: 'recording',
          sampleCount: bench.samples.length,
          summary: null,
          samples: null,
        };

        const cascadeDone = !cascadeLifeRef.current.active;
        const timedOut = wallMs >= SLEEPING_CITY_BENCH_WALL_TIMEOUT_MS;
        if (cascadeDone || timedOut) {
          const brickCount = runtime.brickBodies.length;
          const wallDurationMs = wallMs;
          const simDuration = runtime.stepper.simTime - bench.simStartedAt;
          const wallSec = wallDurationMs / 1000;
          const stepped = bench.steppedStepMs.slice().sort((a, b) => a - b);
          const sampleCount = bench.samples.length;
          const frozenSamples = bench.samples.slice();
          const lastSample = sampleCount > 0 ? frozenSamples[sampleCount - 1] : null;
          const summary = emptyBenchSummary({
            finishReason: sampleCount === 0 ? 'empty' : cascadeDone ? 'cascade_inactive' : 'wall_timeout',
            sampleCount,
            wallDurationMs,
            simDuration,
            timeDilation: wallSec > 0 ? simDuration / wallSec : 0,
            steppedFrameStepMsP50: percentileSorted(stepped, 0.5),
            steppedFrameStepMsP95: percentileSorted(stepped, 0.95),
            steppedFrameStepMsMax: stepped.length ? stepped[stepped.length - 1] : null,
            minFps: Number.isFinite(bench.minFps) ? bench.minFps : null,
            peakAwakeBricks: bench.peakAwakeBricks,
            peakAwakeCount: bench.peakAwakeCount,
            peakStepsPerFrame: bench.peakStepsPerFrame,
            everAwakeCount: runtime.everAwakeCount,
            coverage: brickCount > 0 ? runtime.everAwakeCount / brickCount : 0,
            brickCount,
            fixedDt: runtime.stepper.fixedDt,
            // Actual budgets observed across the whole window (not finish-frame-only).
            substepsUsed: sortedUniqueNumbers(bench.substepsSeen),
            maxStepsUsed: sortedUniqueNumbers(bench.maxStepsSeen),
            // Phase 2 retirement: whole-window min/peak + finals; curve stays frozen for review.
            minDynamicBrickCount:
              sampleCount > 0 && Number.isFinite(bench.minDynamicBrickCount)
                ? bench.minDynamicBrickCount
                : 0,
            finalDynamicBrickCount: lastSample?.dynamicBrickCount ?? 0,
            peakRetiredCount: bench.peakRetiredCount,
            finalRetiredCount: lastSample?.retiredCount ?? 0,
            retirementStartWallMs: bench.retirementStartWallMs,
          });
          // Freeze curve onto public, then clear internal buffers.
          bench.samples = [];
          bench.steppedStepMs = [];
          bench.substepsSeen = [];
          bench.maxStepsSeen = [];
          bench.public = {
            status: 'complete',
            sampleCount,
            summary,
            samples: frozenSamples,
          };
        }
      }
      (window as unknown as { __sleepingCityBench?: SleepingCityBench }).__sleepingCityBench = bench.public;
    }
  });

  return (
    <group>
      <instancedMesh
        ref={brickMeshRef}
        args={[undefined, undefined, Math.max(MAX_BRICKS, bodyLimit)]}
        count={0}
        castShadow
        receiveShadow
        frustumCulled={false}
      >
        <boxGeometry args={[BRICK_FULL.x, BRICK_FULL.y, BRICK_FULL.z]} />
        <meshStandardMaterial roughness={0.8} metalness={0.04} />
      </instancedMesh>
      {Array.from({ length: CHAIN_LINKS }, (_, i) => (
        <mesh
          key={`chain-${i}`}
          ref={(mesh) => {
            chainRefs.current[i] = mesh;
          }}
          castShadow
        >
          <capsuleGeometry args={[LINK_RADIUS, LINK_HALF_HEIGHT * 2, 4, 10]} />
          <meshStandardMaterial color="#6d7480" metalness={0.75} roughness={0.35} />
        </mesh>
      ))}
      <mesh ref={ballMeshRef} castShadow receiveShadow>
        <icosahedronGeometry args={[BALL_RADIUS, 1]} />
        <meshStandardMaterial color="#3a3f46" metalness={0.6} roughness={0.55} flatShading />
      </mesh>
      <instancedMesh ref={cannonMeshRef} args={[undefined, undefined, MAX_CANNONBALLS]} castShadow frustumCulled={false}>
        <sphereGeometry args={[CANNONBALL_RADIUS, 20, 14]} />
        <meshStandardMaterial color="#3a3128" metalness={0.55} roughness={0.4} />
      </instancedMesh>
    </group>
  );
}

function YardCamera({
  controlsRef,
  target = [0.3, 3.2, 0.3],
}: {
  controlsRef: MutableRefObject<OrbitControlsImpl | null>;
  target?: [number, number, number];
}) {
  return (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.08}
      target={target}
      minDistance={6}
      maxDistance={40}
      maxPolarAngle={Math.PI * 0.49}
    />
  );
}

// ---------- HUD ----------

type Cell = [number, number];

function row(y: number, x0: number, x1: number): Cell[] {
  const cells: Cell[] = [];
  for (let x = x0; x <= x1; x += 1) cells.push([x, y]);
  return cells;
}

// Pixel/voxel icon glyphs on a 12x12 grid, drawn with currentColor so the
// active state recolors them via CSS.
const ICON_BALL: Cell[] = [
  ...row(2, 4, 7),
  ...row(3, 3, 8),
  ...row(4, 2, 9),
  ...row(5, 2, 9),
  ...row(6, 2, 9),
  ...row(7, 2, 9),
  ...row(8, 3, 8),
  ...row(9, 4, 7),
].filter(([x, y]) => !(x === 4 && y === 4) && !(x === 7 && y === 7));

const ICON_CANNON: Cell[] = [
  [9, 1], [10, 1], [10, 2],
  [8, 2], [9, 2],
  [7, 3], [8, 3], [9, 3],
  [6, 4], [7, 4], [8, 4],
  [5, 5], [6, 5], [7, 5],
  [4, 6], [5, 6], [6, 6],
  [3, 7], [4, 7], [5, 7],
  [2, 8], [3, 8], [4, 8],
  // carriage + wheel
  ...row(9, 3, 5),
  ...row(10, 3, 5),
  [4, 11],
];

const ICON_BOOM: Cell[] = [
  [5, 5], [6, 5], [5, 6], [6, 6],
  [5, 1], [6, 1], [5, 2], [6, 2],
  [5, 9], [6, 9], [5, 10], [6, 10],
  [1, 5], [1, 6], [2, 5], [2, 6],
  [9, 5], [9, 6], [10, 5], [10, 6],
  [3, 3], [8, 3], [3, 8], [8, 8],
  [2, 2], [9, 2], [2, 9], [9, 9],
];

const ICON_GRAB: Cell[] = [
  [5, 1], [6, 1], [5, 2], [6, 2],
  ...row(3, 4, 7),
  [3, 4], [8, 4],
  [2, 5], [9, 5],
  [2, 6], [9, 6],
  [3, 7], [8, 7],
  [4, 8], [7, 8],
  [5, 5], [6, 5], [5, 6], [6, 6],
];

const ICON_TOWER: Cell[] = [
  [4, 1], [6, 1], [7, 1],
  ...row(2, 4, 7),
  ...row(3, 4, 7),
  ...row(4, 4, 7),
  [4, 5], [7, 5],
  ...row(6, 4, 7),
  ...row(7, 4, 7),
  [4, 8], [7, 8],
  ...row(9, 4, 7),
  ...row(10, 3, 8),
];

const ICON_CASTLE: Cell[] = [
  [2, 1], [3, 1], [8, 1], [9, 1],
  ...row(2, 2, 3), ...row(2, 8, 9),
  ...row(3, 2, 3), ...row(3, 8, 9),
  ...row(4, 2, 3), ...row(4, 8, 9),
  [5, 5], [7, 5],
  ...row(6, 2, 9),
  ...row(7, 2, 9),
  [5, 8], [6, 8],
  ...row(9, 2, 9),
  ...row(10, 2, 9),
];

const ICON_COLOSSEUM: Cell[] = [
  ...row(2, 4, 7),
  [3, 3], [8, 3],
  [2, 4], [9, 4],
  [2, 5], [9, 5],
  [2, 6], [9, 6],
  [2, 7], [9, 7],
  [2, 8], [9, 8],
  [3, 9], [8, 9],
  [4, 10], [7, 10],
  // inner arches
  [4, 7], [6, 7],
  [4, 8], [6, 8],
];

const ICON_STOPWATCH: Cell[] = [
  [5, 0], [6, 0],
  [5, 1], [6, 1],
  ...row(2, 4, 7),
  [3, 3], [8, 3],
  [2, 4], [9, 4],
  [2, 5], [9, 5], [5, 5], [6, 5],
  [2, 6], [9, 6], [5, 6],
  [2, 7], [9, 7],
  [3, 8], [8, 8],
  ...row(9, 4, 7),
  [6, 4], [7, 3],
];

const ICON_RESET: Cell[] = [
  ...row(2, 4, 7),
  [3, 3], [8, 3],
  [2, 4], [9, 4],
  [2, 5], [9, 5],
  [2, 6], [9, 6],
  [3, 7], [8, 7],
  ...row(8, 4, 7),
  // arrowhead breaking the ring, top-right
  [10, 2], [10, 3], [9, 2], [8, 1],
];

function PixelIcon({ cells }: { cells: Cell[] }) {
  return (
    <svg className="wy-icon" viewBox="0 0 12 12" aria-hidden="true" shapeRendering="crispEdges">
      {cells.map(([x, y], i) => (
        <rect key={i} x={x} y={y} width={1} height={1} fill="currentColor" />
      ))}
    </svg>
  );
}

const TOOLS: Array<{ id: YardTool; label: string; icon: Cell[] }> = [
  { id: 'ball', label: 'Ball', icon: ICON_BALL },
  { id: 'cannon', label: 'Cannon', icon: ICON_CANNON },
  { id: 'boom', label: 'Boom', icon: ICON_BOOM },
  { id: 'grab', label: 'Grab', icon: ICON_GRAB },
];

const STRUCTURES: Array<{ id: YardStructure; label: string; icon: Cell[] }> = [
  { id: 'tower', label: 'Tower', icon: ICON_TOWER },
  { id: 'castle', label: 'Castle', icon: ICON_CASTLE },
  { id: 'colosseum', label: 'Colosseum', icon: ICON_COLOSSEUM },
];

function StatBlock({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className="wy-stat">
      <span className="wy-stat-label">{label}</span>
      <span className="wy-stat-value">
        {value}
        {suffix && <em className="wy-stat-suffix">{suffix}</em>}
      </span>
    </div>
  );
}

function SleepProof({ stats }: { stats: YardStats }) {
  const brickTotal = Math.max(stats.bricks, 1);
  const asleepBricks = Math.max(0, stats.bricks - stats.awakeBricks);
  const asleepPct = stats.awakeBricks === 0 ? 100 : Math.floor((asleepBricks / brickTotal) * 100);
  return (
    <aside className="wy-sleep-proof" data-testid="sleep-proof">
      <span className="wy-proof-eyebrow">Sleep proof</span>
      <div className="wy-proof-row">
        <strong>{stats.awakeBricks.toLocaleString('en-US')}</strong>
        <span>awake bricks</span>
      </div>
      <div className="wy-proof-meter" aria-hidden="true">
        <span style={{ width: `${asleepPct}%` }} />
      </div>
      <div className="wy-proof-split">
        <span>idle {stats.idleMs.toFixed(2)} ms</span>
        <span>active {stats.activeMs.toFixed(1)} ms</span>
      </div>
      <span className="wy-proof-asleep">{asleepPct}% asleep</span>
    </aside>
  );
}

export interface WreckingYardProps {
  onBack: () => void;
  initialStructure?: YardStructure;
  bodyLimit?: number;
  title?: string;
  pageClassName?: string;
  hideStructures?: boolean;
  cameraPosition?: [number, number, number];
  cameraTarget?: [number, number, number];
  defaultHeatmap?: boolean;
}

function WreckingYard({
  onBack,
  initialStructure = 'tower',
  bodyLimit = MAX_BRICKS,
  title = 'Wrecking Yard',
  pageClassName = '',
  hideStructures = false,
  cameraPosition = [11.5, 6.6, 13.8],
  cameraTarget = [0.3, 3.2, 0.3],
  defaultHeatmap = false,
}: WreckingYardProps) {
  const [tool, setTool] = useState<YardTool>('ball');
  const [structure, setStructure] = useState<YardStructure>(initialStructure);
  const [slowMo, setSlowMo] = useState(false);
  const [heatmapEnabled, setHeatmapEnabled] = useState(defaultHeatmap);
  const [resetToken, setResetToken] = useState(0);
  const [cascadeToken, setCascadeToken] = useState(0);
  const [physicsReady, setPhysicsReady] = useState(false);
  const [stats, setStats] = useState<YardStats>({ bodies: 0, bricks: 0, awake: 0, awakeBricks: 0, stepMs: 0, idleMs: 0, activeMs: 0, fps: 0 });
  const [boomVignetteKey, setBoomVignetteKey] = useState(0);

  const statsRef = useRef<YardStats>({ bodies: 0, bricks: 0, awake: 0, awakeBricks: 0, stepMs: 0, idleMs: 0, activeMs: 0, fps: 0 });
  const fxRef = useRef<YardFx | null>(null);
  const controlsRef = useRef<OrbitControlsImpl | null>(null);
  const toolRef = useRef<YardTool>('ball');

  const selectTool = useCallback((next: YardTool) => {
    // Update the ref synchronously so a canvas click that lands before the
    // next React render still uses the freshly selected tool.
    toolRef.current = next;
    setTool(next);
  }, []);
  const glowTexture = useMemo(() => makeGlowTexture(), []);
  useEffect(() => () => glowTexture.dispose(), [glowTexture]);

  useEffect(() => {
    writeYardState({
      canvasReady: false,
      physicsReady: false,
      bodyCount: 0,
      brickCount: 0,
      awakeCount: 0,
      structure: initialStructure,
      everAwakeCount: 0,
    });
  }, [initialStructure]);

  useEffect(() => {
    writeYardState({ tool });
  }, [tool]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setStats({ ...statsRef.current });
    }, 250);
    return () => window.clearInterval(interval);
  }, []);

  const handleReady = useCallback(() => {
    setPhysicsReady(true);
  }, []);

  const triggerBoomVignette = useCallback(() => {
    setBoomVignetteKey((key) => key + 1);
  }, []);

  const cursor = tool === 'cannon' || tool === 'boom' ? 'crosshair' : 'grab';
  const isCityScene = initialStructure === 'city' || initialStructure === 'cascade';
  // Marketing capture opts into a readable WebGL backbuffer so the recorder
  // can use canvas.captureStream(). Keep the production path unchanged.
  const captureMode = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('capture') === '1';

  // HUD stats update four times per second. Keep that React state churn from
  // reconciling the full R3F scene; rebuild this element only when a scene input
  // actually changes.
  const yardScene = useMemo(
    () => (
      <Canvas
        shadows
        camera={{ position: cameraPosition, fov: 46 }}
        gl={{ antialias: true, alpha: false, preserveDrawingBuffer: captureMode, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: isCityScene ? 1.28 : 1.15 }}
        style={{ cursor }}
      >
        <color attach="background" args={[isCityScene ? '#8fb7df' : '#060a16']} />
        <fogExp2 attach="fog" args={[isCityScene ? '#9dbbd2' : '#060a16', isCityScene ? 0.0075 : 0.012]} />
        {isCityScene ? <CitySky /> : <Stars />}
        <VoxelClouds />
        <YardCamera controlsRef={controlsRef} target={cameraTarget} />
        <ShakeRig>
          <Lighting city={isCityScene} />
          <Ground />
          <YardDressing />
          {isCityScene && <CityBlockDressing />}
          <Floodlight position={[-10, 0, -3.6]} headY={9.6} target={[0, 4.2, 0]} glowTexture={glowTexture} />
          <Floodlight position={[-7.6, 0, 5.4]} headY={8.2} target={[0.6, 3.2, 0.4]} glowTexture={glowTexture} />
          <Crane />
          <YardPhysicsLayer
            toolRef={toolRef}
            structure={structure}
            initialStructure={initialStructure}
            bodyLimit={bodyLimit}
            slowMo={slowMo}
            heatmapEnabled={heatmapEnabled}
            resetToken={resetToken}
            cascadeToken={cascadeToken}
            statsRef={statsRef}
            fxRef={fxRef}
            controlsRef={controlsRef}
            onReady={handleReady}
          />
          <Effects fxRef={fxRef} glowTexture={glowTexture} onBoomVignette={triggerBoomVignette} />
        </ShakeRig>
      </Canvas>
    ),
    [
      bodyLimit,
      cameraPosition[0],
      cameraPosition[1],
      cameraPosition[2],
      cameraTarget[0],
      cameraTarget[1],
      cameraTarget[2],
      cascadeToken,
      cursor,
      glowTexture,
      handleReady,
      heatmapEnabled,
      initialStructure,
      isCityScene,
      resetToken,
      slowMo,
      structure,
      triggerBoomVignette,
    ],
  );

  return (
    <div className={`wrecking-yard-page ${pageClassName}`.trim()}>
      {yardScene}

      {boomVignetteKey > 0 && <div key={boomVignetteKey} className="wy-boom-vignette" />}

      <div className="wy-overlay">
        <header className="mobile-lab-appbar wy-mobile-appbar" data-testid="yard-appbar">
          <button className="wy-exit" type="button" onClick={onBack} data-testid="yard-back">
            ← Back
          </button>
          <h1 className="mobile-lab-appbar-title">{title}</h1>
          <div className="mobile-lab-appbar-status" data-testid="yard-mobile-stats">
            {Math.round(stats.fps)}fps · {stats.awake} awake
          </div>
        </header>

        <header className="wy-brand">
          <button className="wy-exit" type="button" onClick={onBack}>
            Exit
          </button>
          <h1 className="wy-title">{title}</h1>
          <div className="wy-title-rule" />
        </header>

        <aside className="wy-stats" data-testid="yard-stats">
          <StatBlock label="Bodies" value={stats.bodies.toLocaleString('en-US')} />
          <StatBlock label="Awake" value={stats.awake.toLocaleString('en-US')} />
          <StatBlock label="Step" value={stats.stepMs.toFixed(1)} suffix="ms" />
          <StatBlock label="FPS" value={Math.round(stats.fps).toString()} />
        </aside>

        {isCityScene && <SleepProof stats={stats} />}

        <nav className="wy-tools">
          {TOOLS.map(({ id, label, icon }) => (
            <button
              key={id}
              className={`wy-tool ${tool === id ? 'active' : ''}`}
              data-testid={`tool-${id}`}
              type="button"
              title={id === 'cannon' ? 'Right-click in the scene to fire' : undefined}
              onClick={() => selectTool(id)}
            >
              <PixelIcon cells={icon} />
              <span className="wy-seg-label">{label}</span>
            </button>
          ))}
        </nav>

        {!hideStructures && <nav className="wy-structures">
          {STRUCTURES.filter(({ id }) => id !== 'city').map(({ id, label, icon }) => (
            <button
              key={id}
              className={`wy-structure ${structure === id ? 'active' : ''}`}
              data-testid={`structure-${id}`}
              type="button"
              onClick={() => setStructure(id)}
            >
              <PixelIcon cells={icon} />
              <span className="wy-seg-label">{label}</span>
            </button>
          ))}
        </nav>}

        <div className="wy-side">
          {isCityScene && (
            <button
              className="wy-cascade"
              data-testid="cascade"
              type="button"
              onClick={() => setCascadeToken((token) => token + 1)}
            >
              <span className="wy-slowmo-label">Cascade</span>
            </button>
          )}
          {isCityScene && (
            <button
              className={`wy-heatmap ${heatmapEnabled ? 'active' : ''}`}
              data-testid="sleep-heatmap"
              type="button"
              onClick={() => setHeatmapEnabled((value) => !value)}
            >
              <span className="wy-heatmap-chip" aria-hidden="true" />
              <span className="wy-slowmo-label">Heatmap</span>
            </button>
          )}
          <button
            className={`wy-slowmo ${slowMo ? 'active' : ''}`}
            data-testid="slow-mo"
            type="button"
            onClick={() => setSlowMo((value) => !value)}
          >
            <PixelIcon cells={ICON_STOPWATCH} />
            <span className="wy-slowmo-label">Slow-Mo</span>
          </button>
          <button
            className="wy-reset"
            data-testid="reset-yard"
            type="button"
            aria-label="Reset yard"
            onClick={() => setResetToken((token) => token + 1)}
          >
            <PixelIcon cells={ICON_RESET} />
          </button>
        </div>

        {!physicsReady && <div className="wy-loading">Assembling the yard…</div>}
      </div>
    </div>
  );
}

export default WreckingYard;
